import { useRef, useEffect, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { motion, AnimatePresence } from "framer-motion";
import { Send, StopCircle, Bot, Loader2, ChevronRight } from "lucide-react";
import { ProtectedRoute } from "@/lib/auth-context";
import { getOrRefreshToken } from "@/lib/api";
import { GlassCard } from "@/components/ui";
import { AgentConfirmationCard, extractConfirmation } from "@/components/AgentConfirmationCard";

// ════════════════════════════════════════════════════════════════════════════
// AgentChat — Full-page Flo AI assistant
// ════════════════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const SUGGESTED_PROMPTS = [
  "Show me today's appointments",
  "What's the no-show rate this month?",
  "List all active providers",
  "Summarize this week's performance",
];

function AgentChatInner() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState("");

  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: `${API_BASE}/api/agent/chat`,
      headers: async () => {
        const token = await getOrRefreshToken();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        return headers;
      },
    }),
  });

  const isStreaming = status === "streaming";
  const isSubmitting = status === "submitted";
  const isBusy = isStreaming || isSubmitting;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput("");
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggest = (prompt: string) => {
    if (isBusy) return;
    sendMessage({ text: prompt });
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-af-900 dark:bg-af-100">
          <Bot className="w-5 h-5 text-white dark:text-af-900" />
        </div>
        <div>
          <h1 className="text-base font-semibold text-af-900 dark:text-af-100">Flo</h1>
          <p className="text-xs text-af-500">AI Business Assistant</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center h-full gap-6 text-center"
          >
            <div>
              <h2 className="text-xl font-semibold text-af-900 dark:text-af-100 mb-2">
                Hi, I'm Flo
              </h2>
              <p className="text-af-600 dark:text-af-400 max-w-sm">
                Ask me anything about your appointments, providers, or business performance.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => handleSuggest(prompt)}
                  className="flex items-center gap-2 text-left px-4 py-3 rounded-xl bg-white/10 dark:bg-white/5 border border-white/15 hover:bg-white/20 dark:hover:bg-white/10 transition-colors text-sm text-af-800 dark:text-af-200"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-af-400 shrink-0" />
                  {prompt}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((msg) => {
            const isUser = msg.role === "user";
            const text = (msg.parts ?? [])
              .filter((p): p is { type: "text"; text: string } => p.type === "text")
              .map((p) => p.text)
              .join("\n\n")
              .trim();
            const confirmation = !isUser && msg.parts ? extractConfirmation(msg.parts as unknown[]) : null;

            if (!text && !confirmation && msg.role !== "user") return null;

            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col gap-2 ${isUser ? "items-end" : "items-start"}`}
              >
                <div className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}>
                  {!isUser && (
                    <div className="flex items-end mr-2 mb-1">
                      <div className="w-7 h-7 rounded-lg bg-af-900 dark:bg-af-100 flex items-center justify-center shrink-0">
                        <Bot className="w-4 h-4 text-white dark:text-af-900" />
                      </div>
                    </div>
                  )}
                  {text && (
                    <GlassCard
                      className={`max-w-[75%] px-4 py-3 text-sm ${
                        isUser
                          ? "bg-af-900/90 dark:bg-af-100/90 text-white dark:text-af-900 rounded-2xl rounded-br-sm"
                          : "rounded-2xl rounded-bl-sm text-af-900 dark:text-af-100"
                      }`}
                    >
                      <div className="whitespace-pre-wrap leading-relaxed">{text}</div>
                    </GlassCard>
                  )}
                </div>
                {confirmation && (
                  <div className="ml-9">
                    <AgentConfirmationCard
                      actionId={confirmation.action_id}
                      actionType={confirmation.action_type}
                      preview={confirmation.preview}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {isBusy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2"
          >
            <div className="w-7 h-7 rounded-lg bg-af-900 dark:bg-af-100 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-white dark:text-af-900 animate-spin" />
            </div>
            <span className="text-xs text-af-400">Flo is thinking…</span>
          </motion.div>
        )}

        {error && (
          <div className="text-red-500 text-sm px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
            {error.message}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-white/10">
        <div className="flex items-end gap-2 max-w-4xl mx-auto">
          <GlassCard className="flex-1 flex items-end gap-2 px-4 py-3 rounded-2xl">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Flo anything…"
              rows={1}
              className="flex-1 bg-transparent text-sm text-af-900 dark:text-af-100 placeholder:text-af-400 resize-none outline-none min-h-[1.25rem] max-h-32"
              style={{ fieldSizing: "content" } as React.CSSProperties}
              disabled={isBusy}
            />
          </GlassCard>
          {isBusy ? (
            <button
              onClick={stop}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/30 text-red-500 hover:bg-red-500/30 transition-colors shrink-0"
            >
              <StopCircle className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-af-900 dark:bg-af-100 text-white dark:text-af-900 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-center text-xs text-af-400 mt-2">
          Flo can make mistakes. Verify important information.
        </p>
      </div>
    </div>
  );
}

export default function AgentChat() {
  return (
    <ProtectedRoute>
      <AgentChatInner />
    </ProtectedRoute>
  );
}
